import { formatDanishLongDate } from "@/util/date";
import type {
  AuthorshipEvidenceListModel,
  AuthorshipEvidenceModel,
} from "@cle/models/authorship";
import type {
  CourseCertificateEvidenceListModel,
  CourseCertificateEvidenceModel,
} from "@cle/models/courseCertificate";
import type {
  DispensationEvidenceListModel,
  DispensationEvidenceModel,
} from "@cle/models/dispensation";
import type {
  ReductionEvidenceListModel,
  ReductionEvidenceModel,
} from "@cle/models/reduction";
import type {
  LegalAidEvidenceListModel,
  LegalAidEvidenceModel,
} from "@cle/models/legalAid";
import type {
  TeachingEvidenceListModel,
  TeachingEvidenceModel,
} from "@cle/models/teachingDocument";
import type { SelectableEvidenceRow } from "@cle/models/selectableEvidenceRow";

const MISSING_VALUE = "-";

type CourseCertificateSource =
  CourseCertificateEvidenceListModel | CourseCertificateEvidenceModel;

export const getCourseCertificateEvidenceItemId = (
  certificate: CourseCertificateSource,
): string => certificate.id;

export const mapCourseCertificateToEvidenceRow = (
  certificate: CourseCertificateSource,
): SelectableEvidenceRow => ({
  id: certificate.id,
  evidenceItemId: certificate.id,
  fileName: certificate.fileName ?? MISSING_VALUE,
  lessons: certificate.numberOfLessons ?? 0,
  reported: certificate.isReported,
  detailColumns: [
    {
      id: "courseTitle",
      label: "Kursustitel",
      value: certificate.courseTitle ?? MISSING_VALUE,
    },
    {
      id: "courseProvider",
      label: "Kursusudbyder",
      value: certificate.courseProvider ?? MISSING_VALUE,
    },
    {
      id: "courseDate",
      label: "Kursusdato",
      value: formatDanishLongDate(certificate.courseDate, MISSING_VALUE),
    },
  ],
});

export const mapTeachingToEvidenceRow = (
  teaching: TeachingEvidenceListModel | TeachingEvidenceModel,
): SelectableEvidenceRow => ({
  id: teaching.id,
  evidenceItemId: teaching.id,
  fileName: teaching.fileName ?? MISSING_VALUE,
  lessons: teaching.numberOfLessons ?? 0,
  reported: teaching.isReported,
  detailColumns: [],
});

export const mapDispensationToEvidenceRow = (
  dispensation: DispensationEvidenceListModel | DispensationEvidenceModel,
): SelectableEvidenceRow => ({
  id: dispensation.id,
  evidenceItemId: dispensation.id,
  fileName: dispensation.fileName ?? "Dispensation",
  lessons: dispensation.numberOfLessons ?? 0,
  reported: dispensation.isReported,
  detailColumns: [],
});

export const mapReductionToEvidenceRow = (
  reduction: ReductionEvidenceListModel | ReductionEvidenceModel,
): SelectableEvidenceRow => ({
  id: reduction.id,
  evidenceItemId: reduction.id,
  fileName: reduction.fileName ?? "Reduktion",
  lessons: reduction.numberOfLessons ?? 0,
  reported: reduction.isReported,
  detailColumns: [],
});

export const mapLegalAidToEvidenceRow = (
  legalAid: LegalAidEvidenceListModel | LegalAidEvidenceModel,
): SelectableEvidenceRow => ({
  id: legalAid.id,
  evidenceItemId: legalAid.id,
  fileName: legalAid.fileName ?? "Advokatvagt/retshjælp",
  lessons: legalAid.numberOfLessons ?? 0,
  reported: legalAid.isReported,
  detailColumns: [],
});

export const mapAuthorshipToEvidenceRow = (
  authorship: AuthorshipEvidenceListModel | AuthorshipEvidenceModel,
): SelectableEvidenceRow => ({
  id: authorship.id,
  evidenceItemId: authorship.id,
  fileName: `${authorship.bookTitle} (${authorship.publicationYear})`,
  lessons: authorship.numberOfLessons ?? 0,
  reported: authorship.isReported,
  detailColumns: [
    {
      id: "bookTitle",
      label: "Bogtitel",
      value: authorship.bookTitle ?? MISSING_VALUE,
    },
    {
      id: "publisher",
      label: "Forlag",
      value: authorship.publisher ?? MISSING_VALUE,
    },
    {
      id: "publicationYear",
      label: "Udgivelsesår",
      value: String(authorship.publicationYear),
    },
  ],
});
